package one.healthium.uscreen.ui.blocks

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.tween
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.first
import one.healthium.ui.compose.components.AppAlertDialog
import one.healthium.ui.compose.components.AppLinearProgressIndicator
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.gray_3
import one.healthium.uscreen.domain.models.ProgressSliderBlock
import one.healthium.uscreen.ui.UScreenCallbacks
import one.healthium.uscreen.ui.animation.LocalActiveScreenId
import one.healthium.uscreen.ui.animation.LocalScreenOwnId
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.platform.LocalHapticFeedback
import one.healthium.uscreen.ui.util.uscreenPadding
import kotlin.time.Duration.Companion.seconds

@Composable
fun UScreenProgressSliderBlock(
    modifier: Modifier = Modifier,
    block: ProgressSliderBlock,
    callbacks: UScreenCallbacks,
) {
    val screenOwnId = LocalScreenOwnId.current
    val activeScreenId = LocalActiveScreenId.current
    val isActive = screenOwnId == activeScreenId

    val progresses = remember { block.sliders.map { Animatable(0f) } }
    val showPopup = remember { MutableStateFlow<PopupUiModel?>(null) }
    val haptic = LocalHapticFeedback.current

    LaunchedEffect(isActive) {
        if (!isActive) {
            showPopup.value = null
            return@LaunchedEffect
        }
        // Reset on each activation (handles back-navigation restart)
        progresses.forEach { it.snapTo(0f) }
        showPopup.value = null

        block.sliders.forEachIndexed { index, slider ->
            slider.intervals.forEach { interval ->
                progresses[index].animateTo(
                    targetValue = interval.percentage / 100f,
                    animationSpec = tween(
                        durationMillis = interval.timeInterval.seconds.inWholeMilliseconds.toInt(),
                        easing = LinearEasing,
                    ),
                )
                val popupMessage = interval.popupMessage
                if (!popupMessage.isNullOrBlank()) {
                    haptic.performHapticFeedback(HapticFeedbackType.LongPress)
                    showPopup.value = PopupUiModel(
                        message = popupMessage,
                        positiveButton = interval.answers?.getOrNull(1),
                        negativeButton = interval.answers?.getOrNull(0),
                    )
                    showPopup.first { it == null }
                }
            }
        }
        delay(500)
        if (block.autoAdvance) {
            callbacks.onAutoAdvance()
        }
    }

    if (isActive) {
        SliderPopup(showPopup)
    }

    Column(
        modifier = modifier
            .fillMaxWidth()
            .uscreenPadding(block.padding)
            .testTag("uscreen_progress_slider"),
    ) {
        block.sliders.forEachIndexed { index, slider ->
            if (index > 0) Spacer(Modifier.height(16.dp))
            SliderRow(
                title = slider.title.orEmpty(),
                progress = progresses[index].value,
            )
        }
    }
}

@Composable
private fun SliderRow(
    title: String,
    progress: Float,
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                text = title,
                style = if (progress > 0) AppTheme.textStyles.body1Medium else AppTheme.textStyles.body1,
                color = if (progress > 0) AppTheme.colors.text100 else gray_3,
                modifier = Modifier.weight(1f),
            )
            Text(
                modifier = Modifier.defaultMinSize(minWidth = 56.dp),
                text = "${(progress * 100).toInt()}%",
                style = AppTheme.textStyles.body1,
                textAlign = TextAlign.End
            )
        }
        Spacer(Modifier.height(6.dp))
        AppLinearProgressIndicator(
            modifier = Modifier
                .height(16.dp)
                .fillMaxWidth(),
            progress = { progress },
        )
    }
}

@Composable
private fun SliderPopup(showPopup: MutableStateFlow<PopupUiModel?>) {
    val popUp by showPopup.collectAsState()
    popUp?.let { uiModel ->
        AppAlertDialog(
            positiveButton = uiModel.positiveButton ?: "Ok",
            negativeButton = uiModel.negativeButton,
            text = uiModel.message,
            onPositiveClick = {},
            onNegativeClick = {},
            onDismissRequest = { showPopup.value = null },
        )
    }
}

private data class PopupUiModel(
    val message: String,
    val positiveButton: String?,
    val negativeButton: String?,
)
